import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import WorkshopLinks from '../../components/WorkshopLinks';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconSatellite,
  IconBuildingWarehouse,
  IconChartHistogram,
  IconCheck,
  IconLock,
} from '@tabler/icons-react';

// Adding a partner means adding an entry here. `status` drives what the card
// offers: 'available' connects, 'consent' is an opt-in rather than a
// connection, 'planned' records interest without pretending to connect.
const INTEGRATIONS = [
  {
    key: 'abax',
    icon: IconSatellite,
    colour: '#00A0DF',
    title: 'ABAX Telematics',
    descriptionKey: 'int.abax_desc',
    status: 'planned',
    detailKey: 'int.abax_detail',
  },
  {
    key: 'dms',
    icon: IconBuildingWarehouse,
    colour: 'var(--color-brand)',
    title: 'Agriware DMS',
    descriptionKey: 'int.dms_desc',
    status: 'planned',
    detailKey: 'int.dms_detail',
  },
  {
    key: 'almanac',
    icon: IconChartHistogram,
    colour: '#3B6D11',
    title: 'Agriware Almanac',
    descriptionKey: 'int.almanac_desc',
    status: 'consent',
    detailKey: 'int.almanac_detail',
    consentPointKeys: [
      'int.consent_shared',
      'int.consent_never',
      'int.consent_threshold',
      'int.consent_withdraw',
    ],
  },
];

export default function Integrations() {
  const { t } = useTranslation();
  const { orgId, role } = useAuth();
  const canEdit = role === 'org_admin';

  const [state, setState] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const snap = await getDoc(doc(db, 'organisations', orgId));
        if (snap.exists()) setState(snap.data().integrations || {});
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const write = async (key, value, message) => {
    setBusy(key);
    try {
      await updateDoc(doc(db, 'organisations', orgId), {
        [`integrations.${key}`]: value,
        updatedAt: serverTimestamp(),
      });
      setState(prev => ({ ...prev, [key]: value }));
      toast.success(message);
    } catch (err) {
      console.error(err);
      toast.error(t('int.err_update'));
    }
    setBusy(null);
  };

  const registerInterest = (item) =>
    write(
      item.key,
      { interested: true, registeredAt: new Date().toISOString() },
      t('int.interest_noted')
    );

  const setConsent = (item, on) =>
    write(
      item.key,
      on
        ? { enabled: true, consentedAt: new Date().toISOString() }
        : { enabled: false, withdrawnAt: new Date().toISOString() },
      on ? t('int.almanac_enabled') : t('int.almanac_disabled')
    );

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
          {t('common.loading')}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 680, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 8 }}>{t('set.integrations')}</h1>
        <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
          {t('int.subtitle')}
        </p>

        {!canEdit && (
          <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '10px 12px', marginBottom: 16 }}>
            <IconLock size={15} color="var(--color-text-tertiary)" stroke={1.5} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
              {t('int.admin_only')}
            </div>
          </div>
        )}

        <WorkshopLinks />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {INTEGRATIONS.map(item => {
            const Icon = item.icon;
            const saved = state[item.key] || {};
            const enabled = saved.enabled === true;
            const interested = saved.interested === true;
            const isOpen = expanded === item.key;

            return (
              <Card key={item.key}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 'var(--radius-lg)',
                    background: item.colour + '18',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Icon size={22} color={item.colour} stroke={1.5} />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 14, fontWeight: 400 }}>{item.title}</div>

                      {enabled && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--color-success)', background: 'var(--color-success-light)', padding: '2px 8px', borderRadius: 20 }}>
                          <IconCheck size={11} stroke={2} /> {t('int.enabled')}
                        </div>
                      )}
                      {item.status === 'planned' && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', border: '0.5px solid var(--color-border-medium)', padding: '2px 8px', borderRadius: 20 }}>
                          {t('int.in_development')}
                        </div>
                      )}
                    </div>

                    <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', lineHeight: 1.6, marginBottom: 10 }}>
                      {t(item.descriptionKey)}
                    </div>

                    {isOpen && (
                      <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: item.consentPointKeys ? 10 : 0 }}>
                          {t(item.detailKey)}
                        </div>
                        {item.consentPointKeys && (
                          <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {item.consentPointKeys.map((p, i) => (
                              <li key={i} style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.7 }}>{t(p)}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {item.status === 'consent' ? (
                        <Button
                          size="sm"
                          variant={enabled ? 'danger-outline' : 'secondary'}
                          disabled={!canEdit || busy === item.key}
                          loading={busy === item.key}
                          onClick={() => setConsent(item, !enabled)}
                        >
                          {enabled ? t('int.withdraw') : t('int.enable_almanac')}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!canEdit || busy === item.key || interested}
                          loading={busy === item.key}
                          onClick={() => registerInterest(item)}
                        >
                          {interested ? t('int.interest_registered') : t('int.register_interest')}
                        </Button>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpanded(isOpen ? null : item.key)}
                      >
                        {isOpen ? t('int.less') : t('int.what_this_does')}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 20, lineHeight: 1.6 }}>
          {t('int.footer')}
        </div>
      </div>
    </Layout>
  );
}
